import axiosConfig from "./axiosConfig";
import { Lead } from "./getLeads";

export type LeadImportSourceKind = "csv" | "xlsx";

export interface CsvImportRequest {
  sourceKind: LeadImportSourceKind;
  sourceFilename: string;
  fileContentBase64: string;
  tags?: string[];
  // Portfolio metadata (optional). When `portfolioTag` is set, the BE
  // upserts a `lead_portfolio_metadata` row at kick-off time and the
  // tag is stamped onto every imported lead. The default org (if set)
  // becomes the bulk-create fallback for any lead whose own
  // landlord/tenant decision is unset. The operator picks ONE role at
  // import time - tenant for HSBC-style portfolios where the org
  // occupies every site; landlord for landlord-portfolio uploads.
  portfolioTag?: string;
  portfolioDisplayName?: string;
  defaultTenantOrgId?: string;
  defaultLandlordOrgId?: string;
  // Two-step preview: the first call (without this flag) returns a
  // cost estimate; the second call (with `confirmEnrichment: true`)
  // actually runs the import. Mirrors /leads/search.
  confirmEnrichment?: boolean;
}

// Preview response shape returned when `confirmEnrichment` is false.
// The FE shows a modal with the cost estimate before re-submitting.
export interface CsvImportPreviewResponse {
  status: "preview";
  rowCount: number;
  estimatedCostUsd: number;
  costPerRowUsd: number;
}

export interface CsvImportRowError {
  index: number;
  error: string;
}

// Result shape the long-running operation writes back into DDB on
// completion. Not the immediate response from POST - the FE polls
// `/operations/<key>?siteId=...` and reads `result` once
// `status === "completed"`. Mirrors `BulkCreateOperationResult`.
//
// `rowsAttempted` / `rowsSkipped` exist because there is no row cap
// on the kick-off endpoint: when the worker hits its soft deadline
// (well inside the 15-min Lambda budget) it stops submitting new
// rows and lets the in-flight set drain. `rowsSkipped > 0` means
// the operator should re-upload the tail (rows starting at index
// `rowsAttempted`).
export interface CsvImportOperationResult {
  created: Lead[];
  errors: CsvImportRowError[];
  tags: string[];
  rowsTotal: number;
  rowsAttempted: number;
  rowsSkipped: number;
}

// Immediate response from POST /leads/import/csv: the operation key
// + siteId the FE needs to poll for completion. The actual import
// work happens in the long-running-operation Lambda; the FE polls
// /operations/{key}?siteId=... until status is completed/failed.
export interface CsvImportPendingResponse {
  status: "pending";
  operationKey: string;
  siteId: string;
  rowCount: number;
  tags: string[];
}

export const postLeadImportCsv = async (
  authToken: string,
  body: CsvImportRequest
): Promise<CsvImportPendingResponse | CsvImportPreviewResponse> => {
  const { data } = await axiosConfig.post("/leads/import/csv", body, {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  return data;
};
